/**
 * Contact Submissions Tab Component for Admin Panel
 * Displays contact form submissions from landing page
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Mail, MessageSquare, Building, Clock, CheckCircle, Archive } from 'lucide-react';

export const ContactTab = ({
  submissions,
  stats,
  loading,
  onUpdateStatus
}) => {
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [sortConfig, setSortConfig] = React.useState({ key: 'created_at', direction: 'desc' });
  
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortData = (data) => {
    if (!sortConfig.key || !data) return data;
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (sortConfig.key.includes('created_at') || sortConfig.key.includes('_at')) {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredSubmissions = statusFilter === 'all' 
    ? submissions 
    : submissions.filter(s => s.status === statusFilter);

  const sortedSubmissions = sortData(filteredSubmissions);

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-amber-100 text-amber-800',
      replied: 'bg-green-100 text-green-800',
      archived: 'bg-zinc-100 text-zinc-600'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.new}`}>
        {status}
      </span>
    );
  };

  const getInquiryBadge = (type) => {
    const styles = {
      pricing: 'bg-blue-100 text-blue-800',
      demo: 'bg-purple-100 text-purple-800',
      support: 'bg-amber-100 text-amber-800',
      partnership: 'bg-green-100 text-green-800',
      other: 'bg-zinc-100 text-zinc-600'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[type] || styles.other}`}>
        {type}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Inquiries', value: stats.total || 0, color: 'border-blue-500', textColor: 'text-blue-700', icon: MessageSquare },
          { label: 'New', value: stats.new || 0, color: 'border-amber-500', textColor: 'text-amber-700', icon: Mail },
          { label: 'Replied', value: stats.replied || 0, color: 'border-green-500', textColor: 'text-green-700', icon: CheckCircle }
        ].map((stat, idx) => (
          <Card key={idx} className={`border-l-4 ${stat.color}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-500">{stat.label}</p>
                  <p className={`text-2xl font-bold ${stat.textColor}`}>{stat.value}</p>
                </div>
                <stat.icon className={`w-8 h-8 ${stat.textColor} opacity-20`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Submissions List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Contact Form Submissions</CardTitle>
            <div className="flex items-center gap-2">
              <select
                value={sortConfig.key}
                onChange={(e) => handleSort(e.target.value)}
                className="text-sm border rounded px-2 py-1"
              >
                <option value="created_at">Sort by Date</option>
                <option value="first_name">Sort by Name</option>
                <option value="email">Sort by Email</option>
                <option value="status">Sort by Status</option>
              </select>
              <button 
                onClick={() => setSortConfig(prev => ({ ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' }))}
                className="text-sm border rounded px-2 py-1 hover:bg-zinc-100"
              >
                {sortConfig.direction === 'asc' ? '↑' : '↓'}
              </button>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="text-sm border rounded px-2 py-1"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="replied">Replied</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
              <p>No contact submissions found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedSubmissions.map((submission, idx) => (
                <div key={idx} className="p-4 border border-zinc-200 rounded-lg hover:bg-zinc-50">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-zinc-900">
                          {submission.first_name} {submission.last_name}
                        </h4>
                        {getStatusBadge(submission.status)}
                        {submission.inquiry_type && getInquiryBadge(submission.inquiry_type)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {submission.email}
                        </span>
                        {submission.company && (
                          <span className="flex items-center gap-1">
                            <Building className="w-3 h-3" />
                            {submission.company}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(submission.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {submission.status === 'new' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onUpdateStatus(submission.created_at, 'replied')}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Mark Replied
                        </Button>
                      )}
                      {submission.status !== 'archived' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onUpdateStatus(submission.created_at, 'archived')}
                          className="text-zinc-600 border-zinc-200 hover:bg-zinc-50"
                        >
                          <Archive className="w-3 h-3 mr-1" />
                          Archive
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="p-3 bg-zinc-50 rounded-lg">
                    <p className="text-sm text-zinc-700 whitespace-pre-wrap">{submission.message}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactTab;
